// Session preflight — runs synchronously at boot BEFORE any Supabase
// network call. Prevents the "Loading..." hang class of bugs by
// verifying the persisted JWT's expiration LOCALLY.
//
// The bug we're preventing:
//
//   User signs in on Monday. Supabase writes { access_token, refresh_token,
//   expires_at } to localStorage under `sb-<projectRef>-auth-token`.
//   User comes back on Friday. The access_token expired Wednesday.
//   Supabase JS client calls supabase.auth.getSession() — sees the
//   expired token, kicks off a refresh with the refresh_token. If the
//   refresh call hangs (network, upstream Supabase Auth issue, whatever)
//   getSession() NEVER resolves. Every downstream Supabase call also
//   hangs on the refresh, blocking the app forever.
//
// This module runs FIRST — reads the stored session directly, decodes
// the JWT's `exp` claim, and if it's in the past OR within the safety
// margin, purges the tokens locally BEFORE Supabase even sees them.
// The user lands on the login screen. Zero network round trip. Zero
// hang possible.
//
// Runs at module load in the AuthContext effect.

const SAFETY_MARGIN_SEC = 60;   // treat "expires in <60s" as expired

// Decode a JWT payload without verifying signature. Signature verification
// requires the JWKS which we don't have client-side — but we only need the
// exp claim to decide whether the token is stale. If exp is in the past,
// the token is dead REGARDLESS of signature validity.
function decodeJwtPayload(jwt) {
  try {
    const parts = String(jwt).split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    // Base64url → Base64
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(payload.length + (4 - payload.length % 4) % 4, '=');
    const json = atob(b64);
    return JSON.parse(json);
  } catch { return null; }
}

// Find every localStorage key that Supabase writes session data to.
// Supabase v2 uses `sb-<projectRef>-auth-token`; v1 used
// `supabase.auth.token`. We handle both defensively.
function findSupabaseTokenKeys() {
  const out = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k) continue;
      if (k.startsWith('sb-') && k.endsWith('-auth-token')) out.push(k);
      else if (k.includes('supabase.auth.token')) out.push(k);
    }
  } catch { /* storage disabled */ }
  return out;
}

function purgeAll() {
  try {
    for (const k of findSupabaseTokenKeys()) {
      window.localStorage.removeItem(k);
    }
  } catch { /* ignore */ }
}

/**
 * Run the preflight. Returns:
 *   { ok: true }                  — no session, or session is fresh
 *   { ok: false, reason }         — session was stale; we purged it
 */
export function sessionPreflight() {
  if (typeof window === 'undefined' || !window.localStorage) return { ok: true };

  const keys = findSupabaseTokenKeys();
  if (keys.length === 0) return { ok: true, reason: 'no_session' };

  const now = Math.floor(Date.now() / 1000);

  for (const k of keys) {
    let raw;
    try { raw = window.localStorage.getItem(k); } catch { continue; }
    if (!raw) continue;

    let parsed;
    try { parsed = JSON.parse(raw); } catch { continue; }

    // Supabase v2 stores as either:
    //   { access_token, refresh_token, expires_at, ... }  (bare shape)
    //   { currentSession: {...}, expiresAt: ... }         (older wrapper)
    //   [access_token, refresh_token, ..., expires_at]    (array form seen in some SDK versions)
    const session =
      parsed?.currentSession ||
      (Array.isArray(parsed) ? { access_token: parsed[0], expires_at: parsed[3] } : parsed);

    const accessToken = session?.access_token;
    const expiresAt   = session?.expires_at ?? parsed?.expiresAt;

    if (!accessToken) continue;

    // Prefer the JWT's own exp claim over the wrapper's expires_at —
    // if they disagree, exp is what Supabase Auth actually validates.
    let effectiveExp = null;
    const jwt = decodeJwtPayload(accessToken);
    if (jwt?.exp) effectiveExp = jwt.exp;
    else if (typeof expiresAt === 'number') effectiveExp = expiresAt;

    // No exp claim at all → we can't trust this token. Purge.
    if (effectiveExp == null) {
      purgeAll();
      return { ok: false, reason: 'no_exp_claim' };
    }

    // Past expiration or inside the safety margin → purge before
    // Supabase tries to refresh.
    if (effectiveExp <= now + SAFETY_MARGIN_SEC) {
      purgeAll();
      return { ok: false, reason: `expired_or_stale (exp=${effectiveExp}, now=${now})` };
    }
  }

  return { ok: true, reason: 'session_fresh' };
}

// Extract a minimal Supabase-compatible user object from the persisted JWT.
//
// Used as a fallback by AuthContext when supabase.auth.getSession() takes
// longer than the watchdog to resolve. Before this helper existed, the
// watchdog would nuke the session — logging the user out mid-load — even
// when the persisted token was demonstrably fresh (sessionPreflight said
// so). Now we boot the app with the JWT-derived user and let Supabase's
// background refresh catch up.
//
// The returned shape matches what session.user would have looked like:
//   { id, email, role, aud, app_metadata, user_metadata }
// which is enough for downstream code that reads `user.id`, `user.email`,
// or `user.user_metadata`. Anything that needs the full row (with columns
// only Postgres has, like `banned_until`) will pick it up on the next
// Supabase call after the client finishes its background hydrate.
//
// Returns null if no fresh JWT is present.
export function userFromPersistedJwt() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const keys = findSupabaseTokenKeys();
  if (keys.length === 0) return null;
  const now = Math.floor(Date.now() / 1000);
  for (const k of keys) {
    let raw;
    try { raw = window.localStorage.getItem(k); } catch { continue; }
    if (!raw) continue;
    let parsed;
    try { parsed = JSON.parse(raw); } catch { continue; }
    const session =
      parsed?.currentSession ||
      (Array.isArray(parsed) ? { access_token: parsed[0] } : parsed);
    const token = session?.access_token;
    if (!token) continue;
    const payload = decodeJwtPayload(token);
    if (!payload?.exp || payload.exp <= now) continue;
    // Build the minimal user shape.
    return {
      id:             payload.sub || null,
      email:          payload.email || null,
      role:           payload.role || 'authenticated',
      aud:            payload.aud || 'authenticated',
      app_metadata:   payload.app_metadata || {},
      user_metadata:  payload.user_metadata || {},
    };
  }
  return null;
}

// Export for tests + the tenant-hydration guard that runs the same
// check before its own long await chain.
export { decodeJwtPayload, findSupabaseTokenKeys };
